import { Row } from 'read-excel-file';
import type { DataValidation } from 'exceljs';
import { ExcelTemplateColumn, ExcelTemplateNamedList, ReviewError } from '../../../_shared/models/excel-review.model';
import { downloadExcelTemplateAsync, findClosestName, toCellString, toWholeNumber } from '../../../_shared/helpers/excel-upload.helper';
import { LookupMake, LookupModel, LookupPartCategory, LookupPartType, LookupSupplier, LookupWarehouse } from '../../../_shared/models/lookup.model';
import { PartExcelColumnSettings, PartExcelRow, ReviewPartField, ReviewPartItem } from '../models/part-import.model';

export const PARTS_EXCEL_TEMPLATE_COLUMNS: ExcelTemplateColumn[] = [
  { title: 'Part Name' },
  { title: 'OEM Number' },
  { title: 'Make' },
  { title: 'Model' },
  { title: 'Model Year', subtitle: 'e.g. 2018' },
  { title: 'Description' },
  { title: 'Part Type' },
  { title: 'Category' },
  { title: 'Storage Location' },
  { title: 'Reorder Point', subtitle: 'whole number' },
  { title: 'Reorder Quantity', subtitle: 'whole number' },
  { title: 'Maximum Quantity', subtitle: 'whole number' },
  { title: 'On Hand', subtitle: 'current stock, whole number' },
  { title: 'Unit Cost', subtitle: 'required when On Hand is greater than 0' },
  { title: 'Supplier', subtitle: 'required when On Hand is greater than 0' },
  { title: 'Date Received', subtitle: 'required when On Hand is greater than 0' }
];

export const MIN_MODEL_YEAR = 1900;

export function maxModelYear(): number {
  return new Date().getFullYear() + 1;
}

/** Parses an amount such as "1,250.50"; null for blanks or non-numeric text. */
export function toDecimal(value: string): number | null {
  const text = (value ?? '').trim().replace(/[\s,]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  return Number(text);
}

/** Formats a date as yyyy-MM-dd using the local calendar date. */
export function toIsoDateString(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Excel date cells arrive as Date objects; anything else is kept as typed text. */
export function toCellDateString(cell: unknown): string {
  return cell instanceof Date && !isNaN(cell.getTime()) ? toIsoDateString(cell) : toCellString(cell);
}

/** Accepts yyyy-MM-dd (preferred) or dd/MM/yyyy; null when the text is not a valid date. */
export function parseDateInput(value: string): Date | null {
  const text = (value ?? '').trim();
  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  let year: number, month: number, day: number;
  if (match) {
    [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else {
    match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
    if (!match) return null;
    [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  }
  const date = new Date(year, month - 1, day);
  const isValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isValid ? date : null;
}

export interface PartsTemplateLookups {
  makes: LookupMake[];
  models: LookupModel[];
  partTypes: string[];
  categories: string[];
  storageLocations: string[];
  suppliers: string[];
}

const noModelsListName = 'NoModels';

export function downloadPartsExcelTemplateAsync(lookups: PartsTemplateLookups): Promise<void> {
  const modelsByMake = new Map<number, string[]>();
  for (const model of lookups.models) {
    const models = modelsByMake.get(model.makeId) ?? [];
    if (model.name && !models.some(name => name.toLowerCase() === model.name.toLowerCase())) {
      models.push(model.name);
    }
    modelsByMake.set(model.makeId, models);
  }

  const modelListName = (make: LookupMake): string =>
    (modelsByMake.get(make.id)?.length ?? 0) > 0 ? `Models_${make.id}` : noModelsListName;

  const modelLists: ExcelTemplateNamedList[] = lookups.makes
    .filter(make => modelListName(make) !== noModelsListName)
    .map(make => ({ name: modelListName(make), values: modelsByMake.get(make.id) ?? [] }));

  const wholeNumberValidation = (title: string): DataValidation => ({
    type: 'whole',
    operator: 'greaterThanOrEqual',
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: `Invalid ${title.toLowerCase()}`,
    error: `${title} must be a whole number of 0 or more.`,
    showInputMessage: true,
    promptTitle: title,
    prompt: 'Enter a whole number of 0 or more.',
    formulae: [0]
  });

  return downloadExcelTemplateAsync({
    fileName: 'parts-upload-template.xlsx',
    sheetName: 'Parts Template',
    columns: PARTS_EXCEL_TEMPLATE_COLUMNS,
    columnWidth: 26,
    namedLists: [
      { name: 'Makes', values: lookups.makes.map(make => make.name) },
      { name: 'PartTypes', values: lookups.partTypes },
      { name: 'Categories', values: lookups.categories },
      { name: 'StorageLocations', values: lookups.storageLocations },
      { name: 'Suppliers', values: lookups.suppliers },
      { name: noModelsListName, values: [] },
      ...modelLists
    ],
    lookupTables: [
      { name: 'MakeModelLists', rows: lookups.makes.map(make => [make.name, modelListName(make)]) }
    ],
    validations: [
      {
        column: 3,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Make',
          prompt: 'Pick the make from the dropdown.',
          formulae: ['Makes']
        }
      },
      {
        column: 4,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Model',
          prompt: 'Pick the Make first — this dropdown follows it.',
          formulae: ['INDIRECT(VLOOKUP($C2,MakeModelLists,2,FALSE))']
        }
      },
      {
        column: 5,
        validation: {
          type: 'whole',
          operator: 'between',
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: 'Invalid model year',
          error: `Enter a year between ${MIN_MODEL_YEAR} and ${maxModelYear()}.`,
          showInputMessage: true,
          promptTitle: 'Model Year',
          prompt: 'Enter the four-digit model year, e.g. 2018.',
          formulae: [MIN_MODEL_YEAR, maxModelYear()]
        }
      },
      {
        column: 7,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Part Type',
          prompt: 'Pick the part type from the dropdown.',
          formulae: ['PartTypes']
        }
      },
      {
        column: 8,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Category',
          prompt: 'Pick the part category from the dropdown.',
          formulae: ['Categories']
        }
      },
      {
        column: 9,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Storage Location',
          prompt: 'Pick the storage location (warehouse) from the dropdown.',
          formulae: ['StorageLocations']
        }
      },
      { column: 10, validation: wholeNumberValidation('Reorder Point') },
      { column: 11, validation: wholeNumberValidation('Reorder Quantity') },
      { column: 12, validation: wholeNumberValidation('Maximum Quantity') },
      { column: 13, validation: wholeNumberValidation('On Hand') },
      {
        column: 14,
        validation: {
          type: 'decimal',
          operator: 'greaterThanOrEqual',
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: 'Invalid unit cost',
          error: 'Unit cost must be an amount of 0 or more.',
          showInputMessage: true,
          promptTitle: 'Unit Cost',
          prompt: 'Cost per unit of the opening stock. Optional — only used when On Hand is greater than 0.',
          formulae: [0]
        }
      },
      {
        column: 15,
        validation: {
          type: 'list',
          allowBlank: true,
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Supplier',
          prompt: 'Supplier of the opening stock. Optional — only used when On Hand is greater than 0.',
          formulae: ['Suppliers']
        }
      },
      {
        column: 16,
        validation: {
          type: 'date',
          operator: 'lessThanOrEqual',
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: 'Invalid date',
          error: 'Date received cannot be in the future.',
          showInputMessage: true,
          promptTitle: 'Date Received',
          prompt: 'Date the opening stock was received, e.g. 2026-08-01. Optional — only used when On Hand is greater than 0.',
          formulae: ['TODAY()']
        }
      }
    ],
    columnFormats: [{ column: 16, numFmt: 'yyyy-mm-dd' }],
    highlightedColumns: [3, 4, 7, 8, 9, 15]
  });
}

export const DEFAULT_PARTS_EXCEL_COLUMN_SETTINGS: PartExcelColumnSettings = {
  partNameColumn: 1,
  partNumberColumn: 2,
  makeColumn: 3,
  modelColumn: 4,
  modelYearColumn: 5,
  descriptionColumn: 6,
  partTypeColumn: 7,
  categoryColumn: 8,
  storageLocationColumn: 9,
  reorderPointColumn: 10,
  reorderQuantityColumn: 11,
  maximumQuantityColumn: 12,
  onHandColumn: 13,
  unitCostColumn: 14,
  supplierColumn: 15,
  dateReceivedColumn: 16
};

export function convertRowsToPartExcelRows(rows: Row[], columns: PartExcelColumnSettings): PartExcelRow[] {
  return rows.map(row => ({
    partName: toCellString(row[columns.partNameColumn - 1]),
    partNumber: toCellString(row[columns.partNumberColumn - 1]),
    make: toCellString(row[columns.makeColumn - 1]),
    model: toCellString(row[columns.modelColumn - 1]),
    modelYear: toCellString(row[columns.modelYearColumn - 1]),
    description: toCellString(row[columns.descriptionColumn - 1]),
    partType: toCellString(row[columns.partTypeColumn - 1]),
    category: toCellString(row[columns.categoryColumn - 1]),
    storageLocation: toCellString(row[columns.storageLocationColumn - 1]),
    reorderPoint: toCellString(row[columns.reorderPointColumn - 1]),
    reorderQuantity: toCellString(row[columns.reorderQuantityColumn - 1]),
    maximumQuantity: toCellString(row[columns.maximumQuantityColumn - 1]),
    onHand: toCellString(row[columns.onHandColumn - 1]),
    unitCost: toCellString(row[columns.unitCostColumn - 1]),
    supplier: toCellString(row[columns.supplierColumn - 1]),
    dateReceived: toCellDateString(row[columns.dateReceivedColumn - 1])
  }));
}

export function stripPartsTemplateRows(rows: PartExcelRow[]): PartExcelRow[] {
  const dataRows = rows.length > 0 && isHeaderRow(rows[0]) ? rows.slice(1) : rows;
  return dataRows.filter(row => !isEmptyRow(row));
}

function isHeaderRow(row: PartExcelRow): boolean {
  const partName = row.partName.toLowerCase();
  const partNumber = row.partNumber.toLowerCase();
  return partName === 'part name' || partNumber.startsWith('oem');
}

function isEmptyRow(row: PartExcelRow): boolean {
  return Object.values(row).every(value => !`${value ?? ''}`.trim());
}

export function toReviewPartItems(rows: PartExcelRow[]): ReviewPartItem[] {
  return rows.map(row => ({ ...row, onHand: row.onHand.trim() || '0', errors: [] }));
}

export interface PartValidationLookups {
  makes: LookupMake[];
  modelsByMakeId: Map<number, LookupModel[]>;
  partTypes: LookupPartType[];
  categories: LookupPartCategory[];
  storageLocations: LookupWarehouse[];
  suppliers: LookupSupplier[];
}

export function findByName<T extends { name: string }>(items: T[], name: string): T | undefined {
  const target = name.trim().toLowerCase();
  return target ? items.find(item => item.name.trim().toLowerCase() === target) : undefined;
}

const numericFieldLabels: { field: ReviewPartField; labelKey: string }[] = [
  { field: 'reorderPoint', labelKey: 'parts.import.validation.labels.reorderPoint' },
  { field: 'reorderQuantity', labelKey: 'parts.import.validation.labels.reorderQuantity' },
  { field: 'maximumQuantity', labelKey: 'parts.import.validation.labels.maximumQuantity' },
  { field: 'onHand', labelKey: 'parts.import.validation.labels.onHand' }
];

export function validateReviewPartItem(
  item: ReviewPartItem,
  lookups: PartValidationLookups,
  translate: (key: string, params?: Record<string, unknown>) => string
): ReviewError[] {
  const errors: ReviewError[] = [];
  const missing = (field: ReviewPartField, message: string): void => {
    errors.push({ message, kind: 'missing', field });
  };
  const incorrect = (field: ReviewPartField, message: string, suggestionLabel?: string, suggestionValue?: string | null): void => {
    errors.push({
      message,
      kind: 'incorrect',
      field,
      suggestion: suggestionLabel && suggestionValue ? { field, label: suggestionLabel, value: suggestionValue } : undefined
    });
  };

  if (!item.partName.trim()) {
    missing('partName', translate('parts.import.validation.partNameMissing'));
  }

  if (!item.partNumber.trim()) {
    missing('partNumber', translate('parts.import.validation.partNumberMissing'));
  }

  const makeName = item.make.trim();
  const make = findByName(lookups.makes, makeName);
  if (!makeName) {
    missing('make', translate('parts.import.validation.makeMissing'));
  } else if (!make) {
    incorrect('make', translate('parts.import.validation.makeUnknown', { value: makeName }), translate('parts.import.validation.suggestedMake'), findClosestName(makeName, lookups.makes.map(m => m.name)));
  }

  const modelName = item.model.trim();
  if (!modelName) {
    missing('model', translate('parts.import.validation.modelMissing'));
  } else if (make) {
    const models = lookups.modelsByMakeId.get(make.id);
    if (models && !findByName(models, modelName)) {
      incorrect('model', translate('parts.import.validation.modelUnknown', { value: modelName, make: make.name }), translate('parts.import.validation.suggestedModel'), findClosestName(modelName, models.map(m => m.name)));
    }
  }

  const modelYearText = item.modelYear.trim();
  const modelYear = toWholeNumber(modelYearText);
  if (!modelYearText) {
    missing('modelYear', translate('parts.import.validation.modelYearMissing'));
  } else if (modelYear === null) {
    incorrect('modelYear', translate('parts.import.validation.modelYearInvalid', { value: modelYearText }));
  } else if (modelYear < MIN_MODEL_YEAR || modelYear > maxModelYear()) {
    incorrect('modelYear', translate('parts.import.validation.modelYearRange', { min: MIN_MODEL_YEAR, max: maxModelYear() }));
  }

  if (!item.description.trim()) {
    missing('description', translate('parts.import.validation.descriptionMissing'));
  }

  const partTypeName = item.partType.trim();
  const partType = findByName(lookups.partTypes, partTypeName);
  if (!partTypeName) {
    missing('partType', translate('parts.import.validation.partTypeMissing'));
  } else if (!partType) {
    incorrect('partType', translate('parts.import.validation.partTypeUnknown', { value: partTypeName }), translate('parts.import.validation.suggestedPartType'), findClosestName(partTypeName, lookups.partTypes.map(t => t.name)));
  }

  const categoryName = item.category.trim();
  if (!categoryName) {
    const partTypeCategory = partType ? lookups.categories.find(c => c.id === partType.categoryId) : undefined;
    errors.push({
      message: translate('parts.import.validation.categoryMissing'),
      kind: 'missing',
      field: 'category',
      suggestion: partTypeCategory ? { field: 'category', label: translate('parts.import.validation.suggestedCategory'), value: partTypeCategory.name } : undefined
    });
  } else if (!findByName(lookups.categories, categoryName)) {
    incorrect('category', translate('parts.import.validation.categoryUnknown', { value: categoryName }), translate('parts.import.validation.suggestedCategory'), findClosestName(categoryName, lookups.categories.map(c => c.name)));
  }

  const storageLocationName = item.storageLocation.trim();
  if (!storageLocationName) {
    missing('storageLocation', translate('parts.import.validation.storageLocationMissing'));
  } else if (!findByName(lookups.storageLocations, storageLocationName)) {
    incorrect('storageLocation', translate('parts.import.validation.storageLocationUnknown', { value: storageLocationName }), translate('parts.import.validation.suggestedStorageLocation'),
      findClosestName(storageLocationName, lookups.storageLocations.map(w => w.name)));
  }

  for (const { field, labelKey } of numericFieldLabels) {
    const text = item[field].trim();
    const value = toWholeNumber(text);
    if (!text) {
      missing(field, translate('parts.import.validation.fieldMissing', { label: translate(labelKey) }));
    } else if (value === null || value < 0) {
      incorrect(field, translate('parts.import.validation.fieldNotWholeNumber', { value: text, label: translate(labelKey) }));
    }
  }

  // Opening-stock details are required when there is opening stock to record.
  if ((toWholeNumber(item.onHand) ?? 0) > 0) {
    const unitCostText = item.unitCost.trim();
    const unitCost = toDecimal(unitCostText);
    if (!unitCostText) {
      missing('unitCost', translate('parts.import.validation.unitCostRequired'));
    } else if (unitCost === null || unitCost < 0) {
      incorrect('unitCost', translate('parts.import.validation.unitCostInvalid', { value: unitCostText }));
    }

    const supplierName = item.supplier.trim();
    if (!supplierName) {
      missing('supplier', translate('parts.import.validation.supplierRequired'));
    } else if (!findByName(lookups.suppliers, supplierName)) {
      incorrect('supplier', translate('parts.import.validation.supplierUnknown', { value: supplierName }), translate('parts.import.validation.suggestedSupplier'), findClosestName(supplierName, lookups.suppliers.map(s => s.name)));
    }

    const dateText = item.dateReceived.trim();
    if (!dateText) {
      missing('dateReceived', translate('parts.import.validation.dateReceivedRequired'));
    } else {
      const dateReceived = parseDateInput(dateText);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (!dateReceived) {
        incorrect('dateReceived', translate('parts.import.validation.dateReceivedInvalid', { value: dateText }));
      } else if (dateReceived > today) {
        incorrect('dateReceived', translate('parts.import.validation.dateReceivedFuture'));
      }
    }
  }

  return errors;
}
